import axios, { type AxiosResponse } from 'axios'

import type {
    AddJobTaskPayloadSchema,
    CopyTaskParametersPayloadSchema,
    JobChangeHistoryListResponseSchema,
    JobColourTemplatesListResponseSchema,
    JobDetailResponseSchema,
    JobDieCutterOwnershipTypesListResponseSchema,
    JobDieCuttersListResponseSchema,
    JobElasticMaterialsListResponseSchema,
    JobImpositionPaperResponseSchema,
    JobImpositionResponseSchema,
    JobImpositionStatusesResponseSchema,
    JobMachinesListResponseSchema,
    JobNotesListResponseSchema,
    JobPaperCertificatesListResponseSchema,
    JobPapersListResponseSchema,
    JobPrintColoursListResponseSchema,
    JobProcessesAvailableToAddResponseSchema,
    JobsListResponseSchema,
    JobTaskResponseSchema,
    ParallelComponentsListResponseSchema,
    PrintJobReportsPayloadSchema,
    ReassignComponentsToImpositionsPayloadSchema,
    ReassignComponentsToImpositionsResponseSchema,
    ReprintJobPayloadSchema,
    ReprintJobResponseSchema,
    SaveImpositionPaperPayloadSchema,
    SaveJobPayloadSchema,
    SaveParallelComponentsPayloadSchema,
    SendMaterialsRequirementPayloadSchema
} from '@/api/schemas/jobs/jobs'
import { API_ENDPOINTS } from '@/constants'

class JobsQueries {
    fetchJobs(queryString = ''): Promise<AxiosResponse<JobsListResponseSchema>> {
        const url = `${API_ENDPOINTS.JOBS.LIST}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobsListResponseSchema>(url)
    }

    fetchJob(jobId: number | string): Promise<AxiosResponse<JobDetailResponseSchema>> {
        return axios.get<JobDetailResponseSchema>(`${API_ENDPOINTS.JOBS.DETAILS}/${jobId}`)
    }

    fetchJobNotes(jobId: number): Promise<AxiosResponse<JobNotesListResponseSchema>> {
        return axios.get<JobNotesListResponseSchema>(`${API_ENDPOINTS.JOBS.JOB_NOTES}/${jobId}/job-notes`)
    }

    fetchJobChangeHistory(jobId: number): Promise<AxiosResponse<JobChangeHistoryListResponseSchema>> {
        return axios.get<JobChangeHistoryListResponseSchema>(`${API_ENDPOINTS.JOBS.CHANGE_HISTORY}/${jobId}/change-history`)
    }

    fetchJobImpositionStatuses(jobId: number): Promise<AxiosResponse<JobImpositionStatusesResponseSchema>> {
        return axios.get<JobImpositionStatusesResponseSchema>(API_ENDPOINTS.JOBS.IMPOSITION_STATUSES(jobId))
    }

    fetchJobPrintColours(queryString = ''): Promise<AxiosResponse<JobPrintColoursListResponseSchema>> {
        const url = `${API_ENDPOINTS.JOBS.PRINT_COLOURS}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobPrintColoursListResponseSchema>(url)
    }

    fetchJobColourTemplates(): Promise<AxiosResponse<JobColourTemplatesListResponseSchema>> {
        return axios.get<JobColourTemplatesListResponseSchema>(API_ENDPOINTS.JOBS.COLOUR_TEMPLATES)
    }

    fetchJobMachines(queryString = ''): Promise<AxiosResponse<JobMachinesListResponseSchema>> {
        const url = `${API_ENDPOINTS.JOBS.MACHINES}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobMachinesListResponseSchema>(url)
    }

    fetchJobDieCutters(queryString = ''): Promise<AxiosResponse<JobDieCuttersListResponseSchema>> {
        const url = `${API_ENDPOINTS.JOBS.DIE_CUTTERS}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobDieCuttersListResponseSchema>(url)
    }

    fetchPapers(queryString = ''): Promise<AxiosResponse<JobPapersListResponseSchema>> {
        const url = `${API_ENDPOINTS.PAPERS.LIST}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobPapersListResponseSchema>(url)
    }

    fetchElasticMaterials(queryString = ''): Promise<AxiosResponse<JobElasticMaterialsListResponseSchema>> {
        const url = `${API_ENDPOINTS.MATERIALS.LIST}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobElasticMaterialsListResponseSchema>(url)
    }

    fetchDieCutterOwnershipTypes(queryString = ''): Promise<AxiosResponse<JobDieCutterOwnershipTypesListResponseSchema>> {
        const url = `${API_ENDPOINTS.DIE_CUTTERS.OWNERSHIP_TYPES}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobDieCutterOwnershipTypesListResponseSchema>(url)
    }

    fetchPaperCertificates(queryString = ''): Promise<AxiosResponse<JobPaperCertificatesListResponseSchema>> {
        const url = `${API_ENDPOINTS.PAPERS.CERTIFICATES}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobPaperCertificatesListResponseSchema>(url)
    }

    fetchJobNumbers(queryString = ''): Promise<AxiosResponse<JobsListResponseSchema>> {
        const url = `${API_ENDPOINTS.JOBS.JOB_NUMBERS}${queryString ? `?${queryString}` : ''}`
        return axios.get<JobsListResponseSchema>(url)
    }

    fetchProcessesAvailableToAdd(processId: number): Promise<AxiosResponse<JobProcessesAvailableToAddResponseSchema>> {
        return axios.get<JobProcessesAvailableToAddResponseSchema>(`${API_ENDPOINTS.PRODUCTION_PROCESSES.DETAILS}/${processId}`)
    }

    addTask(payload: AddJobTaskPayloadSchema): Promise<AxiosResponse<JobTaskResponseSchema>> {
        return axios.post<JobTaskResponseSchema>(API_ENDPOINTS.JOBS.IMPOSITION_TASKS, payload)
    }

    saveImposition(payload: JobImpositionResponseSchema): Promise<AxiosResponse<JobImpositionResponseSchema>> {
        return axios.patch<JobImpositionResponseSchema>(`${API_ENDPOINTS.JOBS.IMPOSITIONS}/${payload.id}`, payload)
    }

    saveImpositionPaper(
        impositionId: number,
        payload: SaveImpositionPaperPayloadSchema
    ): Promise<AxiosResponse<JobImpositionPaperResponseSchema>> {
        return axios.patch<JobImpositionPaperResponseSchema>(API_ENDPOINTS.JOBS.IMPOSITION_PAPER(impositionId), payload)
    }

    saveTask(payload: JobTaskResponseSchema): Promise<AxiosResponse<JobTaskResponseSchema>> {
        return axios.patch<JobTaskResponseSchema>(`${API_ENDPOINTS.JOBS.IMPOSITION_TASKS}/${payload.id}`, payload)
    }

    deleteTask(taskId: number): Promise<AxiosResponse<unknown>> {
        return axios.delete<unknown>(`${API_ENDPOINTS.JOBS.IMPOSITION_TASKS}/${taskId}`)
    }

    copyTaskParameters(payload: CopyTaskParametersPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.post<unknown>(API_ENDPOINTS.JOBS.COPY_TASK_PARAMETERS, payload)
    }

    fetchParallelComponents(taskId: number): Promise<AxiosResponse<ParallelComponentsListResponseSchema>> {
        return axios.get<ParallelComponentsListResponseSchema>(
            `${API_ENDPOINTS.JOBS.IMPOSITION_TASKS}/${taskId}/parallel-components`
        )
    }

    saveParallelComponents(
        payload: SaveParallelComponentsPayloadSchema
    ): Promise<AxiosResponse<ParallelComponentsListResponseSchema>> {
        return axios.post<ParallelComponentsListResponseSchema>(API_ENDPOINTS.JOBS.REASSIGN_PARALLEL_COMPONENTS, payload)
    }

    saveJob(payload: SaveJobPayloadSchema): Promise<AxiosResponse<JobDetailResponseSchema>> {
        return axios.patch<JobDetailResponseSchema>(`${API_ENDPOINTS.JOBS.DETAILS}/${payload.id}`, payload, {
            timeout: 0
        })
    }

    sendMaterialsRequirement(payload: SendMaterialsRequirementPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.post<unknown>(API_ENDPOINTS.JOBS.SEND_MATERIAL_REQUIREMENT, {
            jobId: payload.id
        })
    }

    reprintJob(payload: ReprintJobPayloadSchema): Promise<AxiosResponse<ReprintJobResponseSchema>> {
        return axios.post<ReprintJobResponseSchema>(API_ENDPOINTS.JOBS.REPRINT_JOBS, payload)
    }

    reassignComponentsToImpositions(
        payload: ReassignComponentsToImpositionsPayloadSchema
    ): Promise<AxiosResponse<ReassignComponentsToImpositionsResponseSchema>> {
        return axios.post<ReassignComponentsToImpositionsResponseSchema>(
            API_ENDPOINTS.JOBS.REASSIGN_COMPONENTS_TO_IMPOSITIONS,
            payload
        )
    }
    printJobReports(payload: PrintJobReportsPayloadSchema): Promise<AxiosResponse<ArrayBuffer>> {
        return axios.post<ArrayBuffer>(API_ENDPOINTS.JOBS.CARD_PRINT, payload, {
            responseType: 'arraybuffer'
        })
    }

    createJob(payload: Record<string, unknown>): Promise<AxiosResponse<{ id: number }>> {
        return axios.post<{ id: number }>(API_ENDPOINTS.JOBS.CREATE_NEW_JOB, payload, {
            timeout: 0
        })
    }
}

export default new JobsQueries()
